import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/marketing/PublicHeader";
import { PublicFooter } from "@/components/marketing/PublicFooter";
import { FeatureCard } from "@/components/marketing/FeatureCard";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import {
  Search,
  FileCheck,
  TrendingUp,
  Award,
  DollarSign,
  Star,
  Calendar,
  Shield,
  ArrowRight,
} from "lucide-react";

export default function ForVendors() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="For Vendors - Grow Your Business"
        description="Win more work with Radius. Browse RFQs, submit competitive bids, manage projects, and receive guaranteed secure payments."
        canonical="/for-vendors"
      />
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 via-background to-background">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.1),transparent_50%)]" />
        <div className="container relative py-12 md:py-20">
          <div className="mx-auto max-w-4xl text-center space-y-8">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
              Grow Your Business with <span className="text-primary">Quality Opportunities</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Connect with serious buyers, submit competitive bids, and get paid quickly and securely.
            </p>
            <Button size="lg" asChild>
              <Link to="/auth/signup?type=vendor">
                Create Your Vendor Profile <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="bg-muted/50 py-16">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-2">
              <h3 className="font-semibold">Finding Quality Clients</h3>
              <p className="text-sm text-muted-foreground">
                Stop chasing leads. Get matched with serious buyers actively looking for your specific capabilities.
              </p>
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold">Managing Complex Bids</h3>
              <p className="text-sm text-muted-foreground">
                Submit detailed bids with line-item pricing, quantity breaks, and attachments all in one structured format.
              </p>
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold">Payment Delays</h3>
              <p className="text-sm text-muted-foreground">
                Get paid on time through secure, auto-releasing payments. No more chasing invoices or payment delays.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features for Vendors */}
      <section className="container py-20">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl font-bold">Everything You Need to Succeed</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Powerful tools to help you win more business and deliver with confidence
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FeatureCard
            icon={Search}
            title="Discover Relevant RFQs"
            description="Browse RFQs that match your capabilities and certifications. Get invited to private RFQs from clients in your network."
          />
          <FeatureCard
            icon={Award}
            title="Showcase Your Credentials"
            description="Build a comprehensive profile with verified certifications, documented capabilities, past projects, and a photo gallery to stand out from competitors."
          />
          <FeatureCard
            icon={FileCheck}
            title="Submit Detailed Bids"
            description="Create professional bids with line-item pricing, quantity breaks, delivery schedules, and supporting documents."
          />
          <FeatureCard
            icon={Calendar}
            title="Manage Multiple Projects"
            description="Track all your active jobs, milestones, and deadlines in one centralized dashboard with clear visibility."
          />
          <FeatureCard
            icon={Shield}
            title="Secure Transactions"
            description="Stripe Connect ensures fast, secure payments directly to your bank account with full transaction transparency."
          />
          <FeatureCard
            icon={Star}
            title="Build Your Reputation"
            description="Collect reviews from satisfied clients to build credibility and attract more high-quality opportunities."
          />
          <FeatureCard
            icon={TrendingUp}
            title="Grow Your Network"
            description="Get discovered by new clients looking for vendors with your specific certifications and capabilities."
          />
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-muted/50 py-20">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">Why Vendors Love Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                Payment Protection
              </h3>
              <p className="text-sm text-muted-foreground">
                Client payment is secured upfront through milestone-based escrow. Complete your milestones and payment releases automatically—reduced payment uncertainty.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Search className="h-5 w-5 text-primary" />
                Real Opportunities
              </h3>
              <p className="text-sm text-muted-foreground">
                Serious buyers with real projects. Every RFQ comes with detailed specifications and budgets—structured for efficient bidding.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-primary" />
                Reputation Building
              </h3>
              <p className="text-sm text-muted-foreground">
                Every successful project builds your profile with verified reviews and ratings that help you win future opportunities.
              </p>
            </div>
            <div className="p-6 rounded-lg border bg-background">
              <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Project Clarity
              </h3>
              <p className="text-sm text-muted-foreground">
                Clear milestones, deliverables, and deadlines from the start. No scope creep or unclear expectations—just clear work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="container py-20">
        <h2 className="text-3xl font-bold text-center mb-12">Vendor Journey</h2>
        <div className="max-w-3xl mx-auto space-y-8">
          {[
            {
              step: "1",
              title: "Create Your Profile",
              description: "Build a comprehensive profile showcasing your capabilities, certifications, past work, and gallery. Profiles with verified credentials get priority visibility."
            },
            {
              step: "2",
              title: "Find Relevant RFQs",
              description: "Browse public RFQs that match your capabilities or receive private invitations from clients who found your profile. Filter by location, category, and requirements."
            },
            {
              step: "3",
              title: "Submit Your Bid",
              description: "Create a detailed bid with line-item pricing, quantity discounts, delivery schedules, and supporting documentation. Stand out with professional proposals."
            },
            {
              step: "4",
              title: "Get Awarded the Job",
              description: "Clients review all bids and select the winner. Once awarded, payment is secured with purchase protection and you can start work with confidence."
            },
            {
              step: "5",
              title: "Deliver Milestones",
              description: "Complete project milestones on schedule. Track progress, communicate with clients, and manage all documentation in one place."
            },
            {
              step: "6",
              title: "Submit Proof of Delivery",
              description: "For each milestone, submit delivery proof with photos, documents, and required information. Client reviews and approves."
            },
            {
              step: "7",
              title: "Get Paid Automatically",
              description: "Once the client approves your delivery, payment is automatically released to your account. Fast, secure, and hassle-free."
            },
            {
              step: "8",
              title: "Receive Reviews",
              description: "Build your reputation with verified reviews from clients. Great reviews lead to more invitations and opportunities."
            },
          ].map((item) => (
            <div key={item.step} className="flex gap-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xl font-bold">
                {item.step}
              </div>
              <div className="pt-2 space-y-2">
                <h3 className="text-xl font-semibold">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-y bg-primary/5 py-20">
        <div className="container text-center space-y-6">
          <h2 className="text-3xl font-bold">Start Winning More Business Today</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Join our verified vendor marketplace and access bidding opportunities with milestone-based payment protection.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/auth/signup?type=vendor">
                Create Vendor Profile
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/how-it-works/vendors">
                Learn More
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
